import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import type { FormInstance } from 'antd'
import { fetchAuthSession } from 'aws-amplify/auth'
import i18n from '@/i18n'
import { API_EVENTS } from '@/constants'
import type { ApiErrorResponse, ApiFieldError } from './types'
import { clearLocalDevAuth, getLocalDevToken, getSessionVersion, isLocalDevLoginEnabled } from './auth/localDev'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/'

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  // 通常 API の上限。重い処理（出力/印刷など）は各呼び出し側で個別に延長している（例: export 系 600s）。
  timeout: 60_000,
  headers: { 'Content-Type': 'application/json' },
})

type FormFieldPath = string | number | Array<string | number>

interface ApiRequestConfig extends InternalAxiosRequestConfig {
  _retryCount?: number
  _silent?: boolean
  _suppressToastOnFieldErrors?: boolean
}

interface HandledAuthError extends Error {
  __pamsHandledAuthError: true
}

const MAX_RETRIES = 2
const RETRY_DELAY_MS = 1000
const RETRYABLE_STATUS = new Set([408, 502, 503, 504])

const t = (key: string, opts?: Record<string, unknown>): string =>
  String(i18n.t(key as never, opts as never))

apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const localDevToken = getLocalDevToken()
  if (isLocalDevLoginEnabled() && localDevToken && config.headers) {
    config.headers['X-Dev-Auth-Token'] = localDevToken
  } else if (!isLocalDevLoginEnabled()) {
    try {
      const session = await fetchAuthSession()
      const token = session.tokens?.accessToken?.toString()
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch {
      /* not authenticated -- proceed without token */
    }
  }

  const sv = getSessionVersion()
  if (sv !== null && config.headers) {
    config.headers['X-Session-Version'] = String(sv)
  }

  return config
})

function dispatchApiError(message: string) {
  window.dispatchEvent(new CustomEvent(API_EVENTS.API_ERROR, { detail: { message } }))
}

function shouldRetry(error: AxiosError): boolean {
  if (!error.config) return false
  const config = error.config as ApiRequestConfig
  const count = config._retryCount ?? 0
  if (count >= MAX_RETRIES) return false
  if (error.config.method && !['get', 'head', 'options'].includes(error.config.method)) return false
  if (!error.response && error.code !== 'ECONNABORTED') return true
  return error.response ? RETRYABLE_STATUS.has(error.response.status) : false
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function createHandledAuthError(): HandledAuthError {
  const error = new Error('PAMS_HANDLED_AUTH_ERROR') as HandledAuthError
  error.__pamsHandledAuthError = true
  return error
}

function isApiFieldError(value: unknown): value is ApiFieldError {
  return Boolean(
    value &&
      typeof value === 'object' &&
      typeof (value as ApiFieldError).field === 'string' &&
      typeof (value as ApiFieldError).message === 'string',
  )
}

function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
  if (!value || typeof value !== 'object') {
    return false
  }

  const candidate = value as Partial<ApiErrorResponse>
  const fieldErrors = candidate.fieldErrors
  return (
    typeof candidate.status === 'number' &&
    typeof candidate.code === 'string' &&
    typeof candidate.error === 'string' &&
    typeof candidate.message === 'string' &&
    (fieldErrors === undefined || (Array.isArray(fieldErrors) && fieldErrors.every(isApiFieldError)))
  )
}

export function getApiErrorResponse(error: unknown): ApiErrorResponse | null {
  if (!axios.isAxiosError(error)) {
    return null
  }

  const responseData = error.response?.data
  return isApiErrorResponse(responseData) ? responseData : null
}

function resolveErrorMessage(error: AxiosError): string {
  if (!error.response) {
    if (error.code === 'ECONNABORTED') return t('errors:network.timeout')
    if (error.code === 'ERR_NETWORK') return t('errors:network.disconnected')
    return t('errors:network.serverUnreachable')
  }

  const apiError = getApiErrorResponse(error)
  if (apiError?.message) {
    return apiError.message
  }

  const firstFieldError = apiError?.fieldErrors?.find((fieldError) => fieldError.message)?.message
  if (firstFieldError) {
    return firstFieldError
  }

  const statusKey = `errors:http.${error.response.status}`
  const resolved = t(statusKey)
  if (resolved !== statusKey) return resolved

  return t('errors:unknownStatus', { status: error.response.status })
}

export function hasApiFieldErrors(error: unknown): boolean {
  const apiError = getApiErrorResponse(error)
  return Boolean(apiError?.fieldErrors?.length)
}

export function applyApiFieldErrors(
  form: FormInstance,
  error: unknown,
  fieldMap: Record<string, FormFieldPath> = {},
): boolean {
  const apiError = getApiErrorResponse(error)
  const fieldErrors = apiError?.fieldErrors ?? []
  if (fieldErrors.length === 0) {
    return false
  }

  form.setFields(
    fieldErrors.map((fieldError) => ({
      name: fieldMap[fieldError.field] ?? fieldError.field,
      errors: [fieldError.message],
    })),
  )
  return true
}

let handlingUnauthorized = false

async function unwrapBlobErrorBody(error: AxiosError): Promise<void> {
  const data = error.response?.data
  if (!(data instanceof Blob)) return
  const contentType = data.type || ''
  if (!contentType.includes('json') && contentType !== '' && !contentType.includes('text')) {
    return
  }
  try {
    const text = await data.text()
    if (!text) return
    const parsed = JSON.parse(text)
    if (error.response) {
      error.response.data = parsed
    }
  } catch {
    /* leave the original blob in place if it isn't JSON */
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    await unwrapBlobErrorBody(error)
    if (error.response?.status === 401) {
      const url = error.config?.url ?? ''
      if (url.includes('dev-login')) {
        return Promise.reject(error)
      }

      const data = error.response?.data
      if (isApiErrorResponse(data) && data.code === 'SESSION_INVALIDATED') {
        window.dispatchEvent(new CustomEvent(API_EVENTS.SESSION_INVALIDATED))
        return Promise.reject(createHandledAuthError())
      }

      if (!handlingUnauthorized) {
        handlingUnauthorized = true
        if (isLocalDevLoginEnabled()) {
          clearLocalDevAuth()
        }
        window.dispatchEvent(new CustomEvent(API_EVENTS.UNAUTHORIZED))
        setTimeout(() => {
          handlingUnauthorized = false
        }, 2000)
      }
      return Promise.reject(createHandledAuthError())
    }

    // メンテナンス中: 503 + code=MAINTENANCE は再試行せず、即座に sorry 画面へ誘導する。
    // （通常の 503 は従来どおり RETRYABLE_STATUS で再試行する）
    const maintenanceData = error.response?.data
    if (
      error.response?.status === 503 &&
      isApiErrorResponse(maintenanceData) &&
      maintenanceData.code === 'MAINTENANCE'
    ) {
      window.dispatchEvent(new CustomEvent(API_EVENTS.MAINTENANCE))
      return Promise.reject(createHandledAuthError())
    }

    if (shouldRetry(error)) {
      const config = error.config as ApiRequestConfig
      config._retryCount = (config._retryCount ?? 0) + 1
      await delay(RETRY_DELAY_MS * config._retryCount)
      return apiClient.request(config)
    }

    const config = error.config as ApiRequestConfig | undefined
    const silent = config?._silent
    const suppressToastOnFieldErrors = config?._suppressToastOnFieldErrors
    if (!silent && !(suppressToastOnFieldErrors && hasApiFieldErrors(error))) {
      dispatchApiError(resolveErrorMessage(error))
    }

    return Promise.reject(error)
  },
)

export default apiClient

export function isHandledAuthError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    return error.response?.status === 401
  }
  return Boolean(
    error &&
      typeof error === 'object' &&
      '__pamsHandledAuthError' in error &&
      (error as HandledAuthError).__pamsHandledAuthError,
  )
}

export function getErrorMessage(error: unknown, fallback?: string): string {
  const defaultFallback = fallback ?? i18n.t('errors:fallback')

  if (axios.isAxiosError(error)) {
    const apiError = getApiErrorResponse(error)
    if (apiError?.message) {
      return apiError.message
    }

    const firstFieldError = apiError?.fieldErrors?.find((fieldError) => fieldError.message)?.message
    if (firstFieldError) {
      return firstFieldError
    }

    if (error.response && fallback) {
      return fallback
    }

    return resolveErrorMessage(error)
  }

  if (error instanceof Error) return error.message
  return defaultFallback
}
